import { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { PlayerRequirementDialog, PlayerRequirementNotice } from '@/components/ui/SportFormDialog';
import {
  buildGeneratePayload,
  createDefaultTambahGameForm,
  isTambahGameSubmitEnabled,
  minConfirmedPlayers,
  type TambahGameFormState,
} from './badmintonGenerateUtils';
import TambahGameCoreFields from './TambahGameCoreFields';
import TambahGameFormatFields from './TambahGameFormatFields';
import TambahGameTeamsSection, { type TambahGamePlayerOption } from './TambahGameTeamsSection';

interface TambahGameFormProps {
  confirmedCount: number;
  participants: TambahGamePlayerOption[];
  submitting: boolean;
  onSubmit: (form: TambahGameFormState) => void;
  onFormChange?: (form: TambahGameFormState) => void;
  onInsufficientClose?: () => void;
}

export default function TambahGameForm({
  confirmedCount,
  participants,
  submitting,
  onSubmit,
  onFormChange,
  onInsufficientClose,
}: TambahGameFormProps) {
  const [form, setForm] = useState<TambahGameFormState>(createDefaultTambahGameForm);
  const [picker, setPicker] = useState<{ teamIndex: number; slotIndex: number } | null>(null);

  const minPlayers = minConfirmedPlayers(form.playerFormat);
  const enoughPlayers = confirmedCount >= minPlayers;
  const enabled = useMemo(
    () => isTambahGameSubmitEnabled(form, confirmedCount) && !submitting,
    [form, confirmedCount, submitting]
  );
  const playerNotice =
    confirmedCount < 1
      ? 'Belum ada peserta confirmed. Daftarkan pemain di event ini sebelum menambah game.'
      : !enoughPlayers
        ? `Minimal ${minPlayers} peserta confirmed diperlukan untuk game ${form.playerFormat === 'singles' ? 'tunggal' : 'ganda'}.`
        : '';

  const patch = (next: Partial<TambahGameFormState>) => {
    setForm((prev) => {
      const merged = { ...prev, ...next };
      onFormChange?.(merged);
      return merged;
    });
  };

  if (confirmedCount < 1) {
    return (
      <PlayerRequirementDialog
        open
        title="Belum ada pemain terdaftar"
        message={playerNotice}
        onClose={() => onInsufficientClose?.()}
      />
    );
  }

  const fields = (
    <div className="space-y-3 pb-28">
      {playerNotice ? <PlayerRequirementNotice title="Pemain belum cukup" message={playerNotice} /> : null}
      {confirmedCount > 0 && (
        <>
          <TambahGameCoreFields form={form} onChange={patch} />
          <TambahGameFormatFields form={form} confirmedCount={confirmedCount} onChange={patch} />
          {form.rotationFormat === 'round_robin' && (
            <TambahGameTeamsSection
              playerFormat={form.playerFormat}
              teams={form.teams}
              participants={participants}
              picker={picker}
              onPickerChange={setPicker}
              onTeamsChange={(teams) => patch({ teams })}
            />
          )}
        </>
      )}
    </div>
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!enabled) return;
        buildGeneratePayload(form);
        onSubmit(form);
      }}
    >
      {fields}

      {confirmedCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-100 bg-white p-4 pb-safe">
          <Button
            type="submit"
            disabled={!enabled}
            className="h-12 w-full rounded-full bg-[#FC480D] text-sm font-bold text-white hover:bg-[#D83B08] disabled:bg-gray-200 disabled:text-gray-400"
          >
            <Icon icon="mdi:shuffle-variant" className="h-4 w-4" />
            Buat Jadwal
          </Button>
        </div>
      )}
    </form>
  );
}
